import React from 'react';
import { Artifact } from '../../../types/artifact';
import { useTheme } from '../../../contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import 'highlight.js/styles/github-dark.css';
import hljs from 'highlight.js';
import { ArtifactoryMarkdown } from '../../artifactory/ArtifactoryMarkdown';
import { extractMarkdownBody } from '../markdownExtractor';

// Create a custom component to handle mermaid code blocks
const CodeBlock = ({ node, inline, className, children, ...props }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match && match[1] ? match[1] : '';
  
  // Special handling for mermaid code blocks
  if (language === 'mermaid') {
    return (
      <div className="mermaid-diagram">
        <pre className="bg-gray-100 dark:bg-gray-800 p-4 rounded overflow-auto">
          <code className="text-sm">{children}</code>
        </pre>
        <div className="text-xs text-gray-500 mt-1">
          Mermaid diagram (view source)
        </div>
      </div>
    );
  }
  
  // For all other languages, use default rendering
  return !inline ? (
    <pre className={className} {...props}>
      <code className={className} {...props}>
        {children}
      </code>
    </pre>
  ) : (
    <code className={className} {...props}>
      {children}
    </code>
  );
};

interface TextArtifactViewerProps {
  artifact: Artifact;
  // Trailmap L2 (spec §6.1a): when rendered inside an expanded tile,
  // user-authored `# Heading` markdown rebases via `components`
  // override so N expanded markdown tiles don't emit N competing
  // <h1>s in the page outline (WCAG 1.3.1).
  headingShift?: number;
}

export const TextArtifactViewer: React.FC<TextArtifactViewerProps> = ({ artifact, headingShift = 0 }) => {
  const { darkMode } = useTheme();

  // Smart-fallback for object-shape content. Backend wraps non-dict
  // create_artifacts content as {markdown: "..."} (create_artifacts_tool.py
  // line 581) — without this branch the JSON envelope shows raw instead of
  // the rendered body. extractMarkdownBody also catches text/body/content
  // string keys. Falls through to JSON.stringify only when no string body
  // is found.
  const body = extractMarkdownBody(artifact.content);

  // L2 (spec §6.1a): per-level downshift, capped at h6. headingShift=0
  // disables the override (preserves existing L1 behavior).
  const headingComponents: Record<string, keyof JSX.IntrinsicElements> = headingShift > 0 ? {
    h1: `h${Math.min(6, 1 + headingShift)}` as keyof JSX.IntrinsicElements,
    h2: `h${Math.min(6, 2 + headingShift)}` as keyof JSX.IntrinsicElements,
    h3: `h${Math.min(6, 3 + headingShift)}` as keyof JSX.IntrinsicElements,
    h4: `h${Math.min(6, 4 + headingShift)}` as keyof JSX.IntrinsicElements,
    h5: `h${Math.min(6, 5 + headingShift)}` as keyof JSX.IntrinsicElements,
    h6: 'h6',
  } : {};

  return (
    <div className="text-artifact-viewer">
      {typeof artifact.content === 'string' ? (
        <div className={`prose prose-sm max-w-none ${darkMode ? 'prose-invert' : ''}`}>
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[
              [(rehypeHighlight as any), {
                ignoreMissing: true,
                subset: false
              }]
            ]}
            components={{
              code: CodeBlock,
              ...headingComponents,
            }}
          >
            {artifact.content}
          </ReactMarkdown>
        </div>
      ) : body ? (
        <ArtifactoryMarkdown content={body} size="large" headingShift={headingShift} />
      ) : (
        <pre className={`whitespace-pre-wrap text-sm p-3 rounded ${
          darkMode ? 'text-gray-200 bg-gray-900' : 'text-gray-800 bg-gray-50'
        }`}>
          {JSON.stringify(artifact.content, null, 2)}
        </pre>
      )}
    </div>
  );
};
